import {
  AfterViewInit,
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { fromEvent, Subscription } from 'rxjs';
import { auditTime, filter, map } from 'rxjs/operators';

@Directive({
  selector: '[appMatAutocompleteScroll]',
  standalone: true,
})
export class MatAutocompleteScrollDirective implements OnInit, OnDestroy {
  /** Preload distance from the bottom (px) before firing `reachedEnd` */
  @Input() thresholdPx = 100;

  /** Emits whenever the user scrolls within `thresholdPx` of the bottom */
  @Output() reachedEnd = new EventEmitter<void>();

  private panelRoot: HTMLElement | null = null;
  private scrollSub?: Subscription;
  private openedSub?: Subscription;
  private closedSub?: Subscription;

  constructor(private auto: MatAutocomplete) {}

  ngOnInit(): void {
    // Bind/unbind when the autocomplete panel opens/closes
    this.openedSub = this.auto.opened.subscribe(() => this.bind());
    this.closedSub = this.auto.closed.subscribe(() => this.unbind());
  }

  private bind() {
    // The panel element is created in an overlay; defer querying to microtask
    queueMicrotask(() => {
      const panel = this.auto.panel?.nativeElement as HTMLElement | null;
      if (!panel) return;

      // In MDC Material, the scrollable element is usually the inner .mdc-list
      const scrollEl =
        panel.querySelector<HTMLElement>('.mdc-list') ||
        panel.querySelector<HTMLElement>('.mat-mdc-autocomplete-panel') ||
        panel.querySelector<HTMLElement>('.mat-autocomplete-panel') ||
        panel;

      this.panelRoot = scrollEl;

      // Rebind scroll listener
      this.scrollSub?.unsubscribe();
      this.scrollSub = fromEvent(this.panelRoot, 'scroll')
        .pipe(
          auditTime(100),
          map(() => {
            const el = this.panelRoot!;
            // Remaining pixels to the bottom
            const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
            return remaining;
          }),
          // filter(remaining => remaining <= this.thresholdPx),
          filter((remaining) => remaining < 1)
          // filter(remaining => remaining >= this.thresholdPx),
        )
        .subscribe((remaining) => {
          this.reachedEnd.emit();
          console.log('rem', remaining);

          // Save how far we are from bottom *before* adding items
          const d = this.panelRoot ? this.distanceFromBottom() : 0;

          if (this.panelRoot) {
            // If you want to stay exactly where you were:
            this.restoreDistanceFromBottom(d);

            // If you want to avoid being *exactly* at the bottom (prevents immediate re-trigger):
            // this.restoreDistanceFromBottom(d, /*offsetPx=*/ 12);
          }
        });
    });
  }

  private unbind() {
    this.scrollSub?.unsubscribe();
    this.scrollSub = undefined;
    this.panelRoot = null;
  }

  // Utilities inside your component (where you already have `panelRoot: HTMLElement | null`)
  private distanceFromBottom(): number {
    const el = this.panelRoot!;
    return el.scrollHeight - el.scrollTop - el.clientHeight;
  }

  private restoreDistanceFromBottom(distance: number, offsetPx = 0) {
    // Run after DOM updates render the new options
    queueMicrotask(() => {
      const el = this.panelRoot!;
      const target =
        el.scrollHeight -
        el.clientHeight -
        Math.max(0, distance) -
        Math.max(0, offsetPx);
      el.scrollTop = Math.max(0, target);
    });
  }

  ngOnDestroy(): void {
    this.unbind();
    this.openedSub?.unsubscribe();
    this.closedSub?.unsubscribe();
  }
}
